/**
 * WHAT: Landing page visual effects controller
 * WHY: Handle parallax, scroll animations, and interactive effects
 * HOW: Intersection Observer for fade-ins, scroll listeners for parallax
 * IMPORTANCE: Creates stunning, smooth visual experience
 */

// Parallax effect for hero and parallax sections
const initParallax = () => {
    const parallaxElements = document.querySelectorAll('.hero__background, .parallax-section__bg');
    
    if (parallaxElements.length === 0) return;

    const handleScroll = () => {
        const scrolled = window.pageYOffset;
        
        parallaxElements.forEach(element => {
            const rect = element.getBoundingClientRect();
            const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
            
            if (isVisible) {
                // Slow parallax effect (moves 40% of scroll speed)
                const speed = 0.4;
                const yPos = -(scrolled * speed);
                element.style.setProperty('--parallax-offset', `${yPos}px`);
            }
        });
    };

    // Use requestAnimationFrame for smooth performance
    let ticking = false;
    window.addEventListener('scroll', () => {
        if (!ticking) {
            window.requestAnimationFrame(() => {
                handleScroll();
                ticking = false;
            });
            ticking = true;
        }
    }, { passive: true });

    // Initial call
    handleScroll();
};

// Fade-in animations on scroll using Intersection Observer
const initScrollAnimations = () => {
    const animatedElements = document.querySelectorAll('.landing-section, .food-card, .split-section');
    
    if (animatedElements.length === 0) return;

    const observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.15 // Trigger when 15% of element is visible
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('visible');
                // Optional: Stop observing after animation (better performance)
                observer.unobserve(entry.target);
            }
        });
    }, observerOptions);

    animatedElements.forEach(element => {
        observer.observe(element);
    });
};

// Smooth hover effects for food cards
const initFoodCardEffects = () => {
    const foodCards = document.querySelectorAll('.food-card');
    
    foodCards.forEach(card => {
        // Add 3D tilt effect on mouse move
        card.addEventListener('mousemove', (e) => {
            const rect = card.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            
            const centerX = rect.width / 2;
            const centerY = rect.height / 2;
            
            const rotateX = ((y - centerY) / centerY) * -5; // Max 5deg tilt
            const rotateY = ((x - centerX) / centerX) * 5;
            
            card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-12px) scale(1.02)`;
        });
        
        card.addEventListener('mouseleave', () => {
            card.style.transform = '';
        });
    });
};

// Smooth scroll to section
const initSmoothScrollLinks = () => {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
            const href = this.getAttribute('href');
            if (href === '#') return;
            
            e.preventDefault();
            const target = document.querySelector(href);
            
            if (target) {
                const headerOffset = 80;
                const elementPosition = target.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        });
    });
};

// Add loading fade-in effect
const initPageLoad = () => {
    document.body.style.opacity = '0';
    
    window.addEventListener('load', () => {
        document.body.style.transition = 'opacity 0.6s ease';
        document.body.style.opacity = '1';
    });
};

// Initialize all effects
const initLandingEffects = () => {
    // Only run on landing page
    if (!document.body.classList.contains('home')) return;
    
    console.log('Initializing landing page effects...');
    
    initParallax();
    initScrollAnimations();
    initFoodCardEffects();
    initSmoothScrollLinks();
    
    // Add visible class to hero immediately (no delay)
    const hero = document.querySelector('.hero');
    if (hero) {
        hero.classList.add('visible');
    }
    
    console.log('Landing effects initialized successfully');
};

// Export for use in main script
export { initLandingEffects };
